import * as React from "react";
import { PensionType } from "../../../../../../../Wrap/Constants/Enums/Domain/PensionType";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ApplyModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/ApplyModel";
import { GiaBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/GiaBase";
import { IsaBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/IsaBase";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { WrapCashBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/WrapCashBase";
import { HierarchyIdHelper } from "../../../../../../Shared/Common/HierarchyIdHelper";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { Urls } from "../../../../../Urls";
import { SubAccountTypeHelper } from "../../../Common/Helpers/SubAccountTypeHelper";
import { showIncome } from "../../../Common/PaymentsIn/SIPP/ValidationHelper";
import { AdviceChargeReview } from "../../../Common/Review/Component/AdviceChargeReview";
import { DrawdownReview } from "../../../Common/Review/Component/DrawdownReview";
import { IncomePaymentReview } from "../../../Common/Review/Component/IncomePaymentReview";
import { InvestmentReview } from "../../../Common/Review/Component/InvestmentReview";
import { PaymentsInReview } from "../../../Common/Review/Component/PaymentsInReview";
import { WithDrawalReview } from "../../../Common/Review/Component/WithDrawalReview";
import {
    hasEnhancedPensionProtection,
    showChooseAssetstoSell,
} from "../../../Common/SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import { ChooseAssetsToSellFor } from "../../../Common/SIPP/DrawdownDetails/Resource/Contents";

interface IStepReviewProps {
    data: GiaBase | IsaBase | PensionBase | WrapCashBase;
    model: ApplyModel;
    subAccountType: SubAccountType;
}

export const StepReview = (props: IStepReviewProps) => {
    const { data, subAccountType, model } = props;

    const isWithDrawalShow = (): boolean => {
        switch (subAccountType) {
            case SubAccountType.StocksIsa:
                return model.SelectedProducts.find((ele) => SubAccountTypeHelper.isIsa(ele.SubAccountType))
                    ?.IsAddRegularWithdrawalSelected;
            case SubAccountType.InvestmentAccount:
                return model.SelectedProducts.find((ele) => SubAccountTypeHelper.isPP(ele.SubAccountType))
                    ?.IsAddRegularWithdrawalSelected;
            default:
                return false;
        }
    };
    const isAdviserChargeShow = (): boolean => {
        switch (subAccountType) {
            case SubAccountType.WrapCash:
                return false;

            default:
                return true;
        }
    };
    const isIncomePaymentShow = (): boolean => {
        return (
            SubAccountTypeHelper.isOnPlatformPension(subAccountType) &&
            showIncome(
                model.SelectedProducts.find((ele) => SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType)),
                data
            )
        );
    };

    const isDrawDownShow = (): boolean => {
        const pensionType = model.SelectedProducts.find((ele) =>
            SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType)
        )?.PensionType;
        return (
            SubAccountTypeHelper.isOnPlatformPension(subAccountType) &&
            model.PensionApply?.DrawdownModel?.InstructionId != null &&
            pensionType != PensionType.None
        );
    };

    const isShowInvestmentSelection = (): boolean => {
        if (SubAccountTypeHelper.isOnPlatformPension(subAccountType))
            {return (
                (data as PensionBase).PreRetirementInstrumentSelections?.Instruments?.length > 0 ||
                (data as PensionBase).PostRetirementInstrumentSelections?.Instruments?.length > 0
            );}
        return data.InstrumentSelections?.Instruments?.length > 0;
    };

    const getReviewProps = () => {
        return { data: data, subAccountType: subAccountType, process: ProcessType.NB, colNum: 2, extraInfo: model };
    };

    const getWithDrawalReviewProps = () => {
        return { data: data, subAccountType: subAccountType, process: ProcessType.NB, colNum: 2, extraInfo: model };
    };

    const showAssetsToSell = () => {
        return showChooseAssetstoSell((data as PensionBase)?.DrawdownModel, ChooseAssetsToSellFor.Drawdown);
    };

    const isImmediateTaxFreeLumpSum =
        model?.SelectedProducts?.find((ele) => SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType))
            ?.PensionType == PensionType.ImmediateTaxFreeLumpSum;

    return (
        <>
            <PaymentsInReview {...getReviewProps()} />
            {isAdviserChargeShow() && <AdviceChargeReview {...getReviewProps()} />}
            {isShowInvestmentSelection() && (
                <InvestmentReview
                    {...getReviewProps()}
                    ownerHierarchyId={HierarchyIdHelper.generateHierarchyId(data.HierarchyId).entityId}
                    serviceProposition={model?.AccountServiceProposition}
                />
            )}
            {isWithDrawalShow() && <WithDrawalReview {...getWithDrawalReviewProps()} />}
            {isDrawDownShow() && (
                <DrawdownReview
                    isImmediateLumpSum={
                        model.SelectedProducts.find((ele) =>
                            SubAccountTypeHelper.isOnPlatformPension(ele.SubAccountType)
                        )?.PensionType == PensionType.ImmediateTaxFreeLumpSum
                    }
                    {...getReviewProps()}
                    showDrawdownPriceLock={true}
                    showAssetsToSell={showAssetsToSell()}
                    modelData={model}
                    onEditAction={() => Navigator.navigateToAndPreserveQueryParameters(Urls.get().DrawdownNewbusiness)}
                    hasEnhancedPensionProtection={hasEnhancedPensionProtection(model?.PensionApply)}
                />
            )}
            {isIncomePaymentShow() && (
                <IncomePaymentReview
                    {...getReviewProps()}
                    model={model}
                    onEditAction={() => {
                        switch (subAccountType){
                            case SubAccountType.Npr:
                                Navigator.navigateToAndPreserveQueryParameters(Urls.get().NBIncomePaymentsSIPP);
                                break;
                            case SubAccountType.JuniorSipp:
                                Navigator.navigateToAndPreserveQueryParameters(Urls.get().NBIncomePaymentsJSIPP);
                                break;
                        }
                    }}
                    isITFC={isImmediateTaxFreeLumpSum}
                />
            )}
        </>
    );
};
